import { ManifestApiResponse } from '@manifest-cyber/types/interface/apiResponse';
import { SbomEnrichmentSource } from '@manifest-cyber/types/interface/db';
import { InterfaceOrganization } from '@manifest-cyber/types/interface/dbTables';
import {
  rejectFailedRequestAcceptEmptyData,
  rejectFailedRequestOrEmptyData,
} from '../../lib/request/rejectFailedRequest/rejectFailedRequest';
import { AxiosProxy } from '../axiosProxy/axiosProxy';
import { FeatureFlagName } from '../platform/platform.api';
import {
  ChildOrganization,
  MappedChildOrganization,
} from './childOrganization.interface';

const chooseOrganization = async (orgId: string): Promise<void> => {
  const response: ManifestApiResponse<[{ newOrgId: string }]> = await AxiosProxy.get({
    url: `organization/choose/${orgId}`,
  });
  await rejectFailedRequestAcceptEmptyData(response);
};

export interface CreateOrganizationBody {
  name: string;
  domains: string[];
  isPaid: boolean;
  enableInviteOnly: boolean;
}
const createOrganization = async ({
  body,
}: {
  body: CreateOrganizationBody;
}): Promise<InterfaceOrganization> => {
  const response: ManifestApiResponse<[InterfaceOrganization]> = await AxiosProxy.post({
    url: 'admin/organizations',
    withCredentials: true,
    body,
  });

  const validatedRespnse = await rejectFailedRequestOrEmptyData(response);

  return validatedRespnse.data[0];
};

export interface UpdateOrganizationEnableInviteOnlyBody {
  enableInviteOnly?: boolean;
}

const updateOrganizationEnableInviteOnly = async ({
  body,
  id,
}: {
  body: UpdateOrganizationEnableInviteOnlyBody;
  id?: string;
}): Promise<InterfaceOrganization> => {
  const response: ManifestApiResponse<[InterfaceOrganization]> = await AxiosProxy.put({
    url: `organizations/settings/invite-only`,
    withCredentials: true,
    body,
  });

  const validatedRespnse = await rejectFailedRequestOrEmptyData(response);
  return validatedRespnse.data[0];
};

export interface UpdateOrganizationEnrichmentSourceBody {
  enabledSbomEnrichmentSource?: SbomEnrichmentSource | undefined;
}
const updateOrganizationEnrichmentSource = async ({
  body,
  id,
}: {
  body: UpdateOrganizationEnrichmentSourceBody;
  id?: string;
}): Promise<InterfaceOrganization> => {
  const response: ManifestApiResponse<[InterfaceOrganization]> = await AxiosProxy.put({
    url: `organizations/settings/enrichment-source`,
    withCredentials: true,
    body,
  });

  const validatedRespnse = await rejectFailedRequestOrEmptyData(response);
  return validatedRespnse.data[0];
};

export interface UpdateSubOrganizationBody {
  name?: string;
}

const updateSubOrganization = async ({
  body,
  organizationId,
  subOrganizationId,
}: {
  body: UpdateSubOrganizationBody;
  organizationId: string;
  subOrganizationId: string;
}): Promise<InterfaceOrganization> => {
  const response: ManifestApiResponse<[InterfaceOrganization]> = await AxiosProxy.put({
    url: `sub-organizations/${subOrganizationId}`,
    withCredentials: true,
    body,
  });

  const validatedRespnse = await rejectFailedRequestOrEmptyData(response);
  return validatedRespnse.data[0];
};

const getFeatureFlag = async ({
  organizationId,
  name,
}: {
  name: FeatureFlagName;
  organizationId: string;
}): Promise<{ isEnabled: boolean }> => {
  const response = await AxiosProxy.get({
    url: `organizations/feature-flags/${name}`,
  });

  const validatedResponse = await rejectFailedRequestOrEmptyData<{ isEnabled: boolean }>(
    response,
  );
  return validatedResponse.data;
};

const getChildOrganizations = async ({
  currentUserOrganizations,
}: {
  currentUserOrganizations: string[];
}): Promise<MappedChildOrganization[]> => {
  const response = await AxiosProxy.get({
    url: `organization/child`,
  });

  const validatedResponse =
    await rejectFailedRequestOrEmptyData<ChildOrganization[]>(response);

  return validatedResponse.data.map((childOrganization) => {
    return {
      ...childOrganization,
      isCurrentUserOrganization: currentUserOrganizations.includes(childOrganization._id),
    };
  });
};

export const OrganizationApi = {
  chooseOrganization,
  createOrganization,
  updateOrganizationEnableInviteOnly,
  updateOrganizationEnrichmentSource,
  updateSubOrganization,
  getFeatureFlag,
  getChildOrganizations,
};
